import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, FileText, Printer } from "lucide-react";
import { BrandMark, PLATFORM } from "@/components/Brand";
import { useAuth, formatApiError } from "@/auth/AuthContext";

import { nzNow } from "@/lib/dates";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const wrap = "[overflow-wrap:anywhere]";

// The public documents, fetched once per page load.
let cached = null;
const loadLegal = () => (cached ? Promise.resolve(cached) : axios.get(`${API}/public/legal`).then((r) => { cached = r.data; return cached; }));

/** "Terms of Service · Privacy Policy" links for footers and sign-up screens. */
export function LegalLinks({ className = "", testId = "legal-links" }) {
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-4 gap-y-1 ${className}`} data-testid={testId}>
      <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--hp-heading)] underline-offset-2 hover:underline" data-testid="legal-link-terms">Terms of Service</a>
      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--hp-heading)] underline-offset-2 hover:underline" data-testid="legal-link-privacy">Privacy Policy</a>
      <a href="/school-it" target="_blank" rel="noopener noreferrer" className="hover:text-[var(--hp-heading)] underline-offset-2 hover:underline" data-testid="legal-link-school-it">For school IT</a>
    </span>
  );
}

/** The "I agree" checkbox used at sign-up. */
export function AgreeBox({ checked, onChange, testId = "agree-terms" }) {
  return (
    <label className="flex items-start gap-2 text-xs text-[var(--hp-text)] cursor-pointer" data-testid={`${testId}-label`}>
      <input data-testid={testId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
      <span>I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline text-[var(--hp-heading)]">Terms of Service</a> and have read the <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[var(--hp-heading)]">Privacy Policy</a>.</span>
    </label>
  );
}

function Blocks({ blocks }) {
  return blocks.map((b, i) => (typeof b === "string"
    ? <p key={i} className={`text-sm leading-relaxed text-[var(--hp-text)] ${wrap}`}>{b}</p>
    : <ul key={i} className="list-disc pl-5 space-y-1 text-sm leading-relaxed text-[var(--hp-text)]">{b.ul.map((li, j) => <li key={j} className={wrap}>{li}</li>)}</ul>));
}

function Document({ kind, data }) {
  const sections = data[kind];
  const title = kind === "terms" ? "Terms of Service" : "Privacy Policy";
  return (
    <article data-testid={`legal-doc-${kind}`}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--hp-profit)]">{data.platform}</p>
      <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--hp-heading)] mt-1" data-testid="legal-title">{title}</h1>
      <p className="text-xs text-[var(--hp-muted)] mt-2" data-testid="legal-updated">Last updated {data.updated}</p>
      <nav className="mt-6 rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-4" aria-label="Contents" data-testid="legal-toc">
        <p className="text-xs font-semibold uppercase tracking-wider text-[var(--hp-muted)] mb-2">Contents</p>
        <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">{sections.map((s) => <li key={s.id}><a href={`#${kind}-${s.id}`} className="text-[var(--hp-text)] hover:text-[var(--hp-heading)] hover:underline">{s.title}</a></li>)}</ol>
      </nav>
      <div className="mt-8 space-y-8">
        {sections.map((s) => (
          <section key={s.id} id={`${kind}-${s.id}`} className="space-y-3 scroll-mt-20" data-testid={`legal-section-${s.id}`}>
            <h2 className="font-head text-xl font-bold text-[var(--hp-heading)]">{s.title}</h2>
            <Blocks blocks={s.body} />
          </section>
        ))}
      </div>
    </article>
  );
}

/** /terms and /privacy: public pages, no sign-in needed. */
export default function Legal() {
  const path = window.location.pathname.replace(/\/+$/, "");
  const kind = path === "/privacy" ? "privacy" : "terms";
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { loadLegal().then(setData).catch((e) => setErr(formatApiError(e.response?.data?.detail, "Couldn't load this page — please try again."))); }, []);
  useEffect(() => { document.title = `${kind === "privacy" ? "Privacy Policy" : "Terms of Service"} – ${PLATFORM.name}`; }, [kind]);
  return (
    <div className="min-h-screen bg-[var(--hp-bg)]" data-testid="legal-page">
      <header className="border-b border-[var(--hp-border)] print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
          <a href="/" aria-label="Home"><BrandMark size="md" /></a>
          <nav className="flex items-center gap-2 text-sm font-semibold" aria-label="Legal">
            {[["terms", "Terms of Service", FileText], ["privacy", "Privacy Policy", ShieldCheck]].map(([k, l, Icon]) => (
              <a key={k} href={`/${k}`} data-testid={`legal-tab-${k}`} aria-current={kind === k ? "page" : undefined} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 ${kind === k ? "bg-[var(--hp-primary)] text-[#04231a]" : "border border-[var(--hp-border-hi)] text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]"}`}><Icon className="h-3.5 w-3.5" />{l}</a>
            ))}
            <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hp-border-hi)] px-3.5 py-1.5 text-[var(--hp-heading)] hover:bg-[var(--hp-tint-5)]" data-testid="legal-print"><Printer className="h-3.5 w-3.5" />Print</button>
          </nav>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10">
        {err && <p className="rounded-xl border border-[var(--hp-loss)]/40 bg-[var(--hp-loss)]/10 px-3 py-2 text-sm text-[var(--hp-loss)]" data-testid="legal-error">{err}</p>}
        {!data && !err && <p className="text-sm text-[var(--hp-muted)] inline-flex items-center gap-2" data-testid="legal-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>}
        {data && <Document kind={kind} data={data} />}
      </main>
      <footer className="border-t border-[var(--hp-border)] print:hidden">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-3 text-xs text-[var(--hp-muted)]">
          <p>© {nzNow().getFullYear()} {PLATFORM.name}</p>
          <a href="/" className="font-semibold hover:text-[var(--hp-heading)]">Back to home</a>
        </div>
      </footer>
    </div>
  );
}

/** A signed-in user who has not accepted the current Terms / Privacy Policy is asked here, before anything else. */
export function AcceptTerms() {
  const { user, logout, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const currentVersion = user.legal.current;
  useEffect(() => { loadLegal().then(setData).catch(() => setData({ version: currentVersion, updated: "" })); }, [currentVersion]);
  const accept = async () => {
    setBusy(true); setErr("");
    try { await axios.post(`${API}/legal/accept`, { version: data?.version || user.legal.current }); cached = null; await refresh(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail, "Couldn't save that — please try again.")); } finally { setBusy(false); }
  };
  const again = !!user.legal.accepted;
  return (
    <div className="min-h-screen grain bg-[var(--hp-bg)] grid place-items-center px-4 py-10" data-testid="accept-terms-page">
      <div className="w-full max-w-md rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-7 shadow-2xl space-y-5">
        <BrandMark size="lg" />
        <div>
          <h1 className="font-head text-2xl font-bold text-[var(--hp-heading)]">{again ? "We've updated our terms" : "One quick step"}</h1>
          <p className="text-sm text-[var(--hp-text)] mt-2">{again ? "Please read and accept the updated Terms of Service and Privacy Policy to carry on." : `Before you carry on, please read and accept the ${PLATFORM.name} Terms of Service and Privacy Policy.`}</p>
          {data?.updated && <p className="text-xs text-[var(--hp-muted)] mt-1">Last updated {data.updated}</p>}
        </div>
        <AgreeBox checked={agree} onChange={setAgree} testId="accept-terms-checkbox" />
        {err && <p className="rounded-xl border border-[var(--hp-loss)]/40 bg-[var(--hp-loss)]/10 px-3 py-2 text-xs text-[var(--hp-loss)]" data-testid="accept-terms-error">{err}</p>}
        <div className="flex items-center gap-3">
          <button data-testid="accept-terms-submit" onClick={accept} disabled={!agree || busy} className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[var(--hp-primary)] px-5 py-2.5 text-sm font-bold text-[#04231a] hover:bg-[var(--hp-primary-hover)] disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Accept and continue</button>
          <button data-testid="accept-terms-signout" onClick={logout} className="text-xs text-[var(--hp-muted)] underline hover:text-[var(--hp-heading)]">Sign out</button>
        </div>
      </div>
    </div>
  );
}
